import { FC, useRef } from 'react';
import Page, { PageProps } from '../../components/page';
import { Box, Stack } from '@mui/material';
import ThreadList from './thread-list';
import { createUseStyles } from 'react-jss';
import MessageControl, { MessageControlInstance } from './message-control';
import ThreadOverlay from './thread-overlay';
import WelcomeMessage from './welcome-message';
import FileDropzone from '@/components/file-dropzone';
import FollowButton from './follow-button';
import { ScrollProvider } from '@/contexts/scroll';
import useThread from '@/hooks/use-thread.hook';
import { getBreakpoint } from '@/theme';

const useStyles = createUseStyles({
  chatPage: {
    '& .page-inner': {
      height: '100%',
    },
  },
  threadPanel: {
    flexGrow: 1,
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  scrollContext: {
    width: '100%',
    minWidth: 0,
    height: '100%',
    boxSizing: 'border-box',
    paddingTop: 10,
    overflowY: 'auto',
    overflowAnchor: 'none',
  },
  contentContainer: {
    width: '100%',
    minWidth: 0,
    maxWidth: 1200,
    padding: '0px 30px',
    boxSizing: 'border-box',
    margin: '0 auto',
  },
  messageControlContainer: {
    position: 'relative',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '20px 0px',
    boxSizing: 'border-box',
  },
  [getBreakpoint('md')]: {
    contentContainer: {
      padding: '0px 8px',
    },
    messageControlContainer: {
      padding: '10px 0px',
    },
  },
});

const ChatPage: FC<PageProps> = () => {
  const styles = useStyles();

  const { thread } = useThread();

  const messageControlRef = useRef<MessageControlInstance>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleCloseWelcomeMessage = () => {
    messageControlRef.current?.focus();
  };

  const handleDrop = (files: FileList) => {
    messageControlRef.current?.loadFiles(files);
  };

  return (
    <Page className={styles.chatPage} title="Chat">
      <Stack
        justifyContent="center"
        alignItems="flex-start"
        width="100%"
        height="100%"
        position="relative"
      >
        <FileDropzone className={styles.threadPanel} onDrop={handleDrop}>
          <ScrollProvider scrollRef={scrollRef} key={`scroll-thread-${thread.id}`}>
            <Box className={styles.scrollContext} id="thread-list" ref={scrollRef}>
              <Box className={styles.contentContainer} minHeight="100%">
                <ThreadList messageControlRef={messageControlRef} />
              </Box>
            </Box>
            <FollowButton />
          </ScrollProvider>
          <ThreadOverlay />
        </FileDropzone>
        <Box className={styles.messageControlContainer}>
          <Box className={styles.contentContainer}>
            <MessageControl ref={messageControlRef} />
          </Box>
        </Box>
        <WelcomeMessage onClose={handleCloseWelcomeMessage} />
      </Stack>
    </Page>
  );
};

export default ChatPage;
